import { useAuth } from "@/src/contexts/auth.context";
import { formatDisplayDate } from "@/src/utils/date.utils";
import { GroupMember } from "@/src/models/user.model";
import { PagyMeta } from "@/src/screens/groups/group-members";
import { useRouter } from "expo-router";
import { Trash } from "iconsax-react-nativejs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Image,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { DataTable } from "react-native-paper";
import { useTheme } from "@/src/styles/theme-provider";
import ConfirmModal from "../../action-modals/confirm-modal";
import ScPagination from "../../ui-kit/pagination";
import StateDisplay from "../../ui-kit/state-display";
import BlocksAvatar from "../../ui-kit/blocks-avatar";
import MemberRowSkeleton from "./member-row-skeleton";

type GroupMembersTableProps = Readonly<{
  members: GroupMember[];
  membersPagy?: PagyMeta;
  onMembersPageChange: (page: number) => void;
  groupId: string;
  isAdmin: boolean;
  isActionPending: boolean;
  handleRemoveMembership: (
    membershipId: string,
    toastKey: string,
    removedUserId?: string, // optional param
  ) => void;
  isLoading?: boolean;
}>;

const COL = {
  name: { flex: 2 },
  email: { flex: 3 },
  role: { flex: 1 },
  date: { flex: 1.5 },
  delete: { flex: 0.5, justifyContent: "center" },
} as const;

const AllMembers = ({
  members,
  membersPagy,
  onMembersPageChange,
  groupId,
  isAdmin,
  isActionPending,
  handleRemoveMembership,
  isLoading = false,
}: GroupMembersTableProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { themeVars } = useTheme();
  const { user } = useAuth();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const [selectedMember, setSelectedMember] = useState<GroupMember | null>(
    null,
  );

  const goToProfile = (member: GroupMember) =>
    router.push({
      pathname: "/profile/[id]",
      params: {
        id: member.itemId,
        firstName: member.firstName,
        lastName: member.lastName,
        role: member.role,
        email: member.email,
        url: member.profileImageUrl,
      },
    });

  return (
    <View className="flex-1">
      {isMobile ? (
        /* ── Mobile: card list ── */
        <View className="gap-3 pt-1">
          {isLoading ? (
            <View className="py-10 items-center">
              <ActivityIndicator
                color={themeVars["--color-primary-default"] as string}
              />
            </View>
          ) : members.length === 0 ? (
            <StateDisplay
              descriptionLabel="LABEL_GROUP_MEMBER_LIST_EMPTY"
              imgSource={require("@/src/assets/images/empty.png")}
            />
          ) : (
            members.map((member) => (
              <View
                key={member.itemId}
                className="bg-chalk rounded-xl p-3 gap-3"
              >
                <View className="flex-row items-center justify-between gap-3">
                  <Pressable
                    className="flex-row items-center gap-3 flex-1 active:opacity-70 hover:bg-ash-3 hover:rounded-lg"
                    onPress={() => goToProfile(member)}
                  >
                    <BlocksAvatar
                      userId={member.userId}
                      fallbackImageUrl={member.profileImageUrl}
                      size={40}
                    />
                    <View className="flex-1">
                      <Text
                        className="text-sm font-semibold text-onyx"
                        numberOfLines={1}
                      >
                        {member.firstName} {member.lastName}
                      </Text>
                      <Text className="text-xs text-slate" numberOfLines={1}>
                        {member.email}
                      </Text>
                    </View>
                  </Pressable>

                  {isAdmin && member.userId !== user?.itemId && (
                    <Pressable
                      onPress={() => setSelectedMember(member)}
                      disabled={isActionPending}
                      hitSlop={8}
                      className="active:opacity-70 hover:bg-ash-3 hover:rounded-full"
                    >
                      {isActionPending &&
                      selectedMember?.itemId === member.itemId ? (
                        <ActivityIndicator size="small" />
                      ) : (
                        <Trash
                          size={20}
                          color={themeVars["--color-crimson-default"] as string}
                        />
                      )}
                    </Pressable>
                  )}
                </View>

                <View className="border-t border-ash-3" />

                <View className="flex-row justify-between flex-wrap gap-2">
                  <View className="gap-0.5">
                    <Text className="text-xs text-slate">{t("LABEL_ROLE")}</Text>
                    <Text className="text-xs font-medium text-primary">
                      {member.role}
                    </Text>
                  </View>
                  <View className="gap-0.5">
                    <Text className="text-xs text-slate">
                      {t("LABEL_DATE_JOINED")}
                    </Text>
                    <Text className="text-xs font-medium text-onyx">
                      {formatDisplayDate(member.joinedAt)}
                    </Text>
                  </View>
                </View>
              </View>
            ))
          )}
        </View>
      ) : (
        /* ── Desktop: full table ── */
        <DataTable>
          <DataTable.Header
            style={{
              borderBottomColor: themeVars["--color-ash-3"] as string,
              borderBottomWidth: 1,
            }}
          >
            <DataTable.Title style={COL.name}>
              {t("LABEL_NAME")}
            </DataTable.Title>
            <DataTable.Title style={COL.email}>
              {t("LABEL_EMAIL")}
            </DataTable.Title>
            <DataTable.Title style={COL.role}>
              {t("LABEL_ROLE")}
            </DataTable.Title>
            <DataTable.Title style={COL.date}>
              {t("LABEL_DATE_JOINED")}
            </DataTable.Title>
            <DataTable.Title style={COL.delete}>
              {t("LABEL_ACTION")}
            </DataTable.Title>
          </DataTable.Header>

          {isLoading ? (
            <MemberRowSkeleton count={5} />
          ) : members.length === 0 ? (
            <DataTable.Row>
              <DataTable.Cell style={{ flex: 1 }}>
                <StateDisplay
                  descriptionLabel="LABEL_GROUP_MEMBER_LIST_EMPTY"
                  imgSource={require("@/src/assets/images/empty.png")}
                />
              </DataTable.Cell>
            </DataTable.Row>
          ) : (
            <>
              {members.map((member) => (
                <DataTable.Row
                  style={{
                    borderBottomColor: themeVars["--color-ash-3"] as string,
                    borderBottomWidth: 1,
                  }}
                  key={member.itemId}
                >
                  <DataTable.Cell style={COL.name}>
                    <Pressable
                      className="cursor-pointer flex-row items-center gap-2 overflow-ellipsis flex-1 active:opacity-70 hover:bg-ash-3 hover:rounded-lg"
                      onPress={() => goToProfile(member)}
                    >
                      <BlocksAvatar
                        className="hidden sm:flex"
                        userId={member.userId}
                        fallbackImageUrl={member.profileImageUrl}
                        size={32}
                      />
                      <Text className="flex-1 overflow-ellipsis text-onyx">
                        {member.firstName} {member.lastName}
                      </Text>
                    </Pressable>
                  </DataTable.Cell>
                  <DataTable.Cell style={COL.email}>
                    {member.email}
                  </DataTable.Cell>
                  <DataTable.Cell style={COL.role}>
                    <Text style={{ color: themeVars["--color-primary-default"] as string }}>
                      {member.role}
                    </Text>
                  </DataTable.Cell>
                  <DataTable.Cell style={COL.date}>
                    {formatDisplayDate(member.joinedAt)}
                  </DataTable.Cell>
                  <DataTable.Cell style={COL.delete}>
                    {isAdmin && member.userId !== user?.itemId ? (
                      <Pressable
                        onPress={() => setSelectedMember(member)}
                        disabled={isActionPending}
                        className="active:opacity-70 hover:bg-ash-3 hover:rounded-full"
                      >
                        {isActionPending &&
                        selectedMember?.itemId === member.itemId ? (
                          <ActivityIndicator size="small" />
                        ) : (
                          <Trash color={themeVars["--color-crimson-default"] as string} />
                        )}
                      </Pressable>
                    ) : (
                      <Text className="text-slate">-</Text>
                    )}
                  </DataTable.Cell>
                </DataTable.Row>
              ))}
            </>
          )}
        </DataTable>
      )}

      {membersPagy && (
        <ScPagination
          currentPage={membersPagy.current_page}
          totalPages={membersPagy.total_pages}
          totalItems={membersPagy.total_count}
          from={membersPagy.from}
          to={membersPagy.to}
          onPageChange={onMembersPageChange}
        />
      )}

      <ConfirmModal
        visible={selectedMember !== null}
        title="LABEL_DELETE_MEMBER"
        description="LABEL_DELETE_MEMBER_DESC"
        confirmText="BTN_YES"
        cancelText="BTN_CANCEL"
        onCancel={() => setSelectedMember(null)}
        onConfirm={() => {
          if (selectedMember) {
            handleRemoveMembership(
              selectedMember.itemId,
              "LABEL_MEMBER_REMOVED",
              selectedMember.userId, // pass userId so notification can be sent
            );
          }
          setSelectedMember(null);
        }}
      />
    </View>
  );
};

export default AllMembers;
